import React, { useCallback, useEffect, useRef, useState } from "react"
import { Box, Button, Nav, Text, Grommet } from "grommet"
import { navigate } from "gatsby"
import whatIOfferData from "../data/whatIOfferData"
import styles from "./sidebarNav.module.scss"

// Color tokens
const ACCENT = "#8fc1e3"
const TEXT_COLOR = "hsla(0,0%,0%,0.85)"
const DESKTOP_LABEL_COLOR = "white"

// Background & interaction tokens
const HOVER_BG = "rgba(103,120,116,0.06)"
const SELECT_BG = "rgba(143,193,227,0.12)"
const NAV_BG = "rgba(143,193,227,0.12)"
const NAV_BG_OPAQUE = "rgba(143,193,227,0.9)"

// Layout / spacing tokens (px or grommet size tokens)
const TAB_PAD_HORIZONTAL = "6px"
const TAB_PAD_VERTICAL = "4px"
const TAB_BORDER_RADIUS = 4
const TAB_SELECTED_BORDER_THICKNESS = 3 // px used in template
const NAV_PADDING_BOTTOM = 4

// Breakpoints (px)
const NARROW_LABEL_BREAKPOINT = 590

// Text / sizes
const TEXT_SIZE_LARGE = "large"
const TEXT_SIZE_SMALL = "small"

// Desktop nav tokens
const DESKTOP_STICKY_TOP = "60px"
const DESKTOP_NAV_BORDER = "1px solid rgba(0,0,0,0.04)"
const DESKTOP_NAV_Z = 0

// Mobile nav tokens
const MOBILE_STICKY_TOP = "60px"
const MOBILE_NAV_Z = 2

// Panel / content token
const PANEL_PAD = "small"

// Desktop SidebarButton pad tokens (grommet size keywords)
const SIDEBAR_BUTTON_PAD_HORIZONTAL = "medium"
const SIDEBAR_BUTTON_PAD_VERTICAL = "medium"

const SidebarButton = ({ label, active = false, ...rest }) => (
  <Button plain {...rest}>
    {({ hover }) => {
      const bg = active ? SELECT_BG : hover ? HOVER_BG : undefined
      const color = active ? ACCENT : TEXT_COLOR
      return (
        <Box
          background={bg}
          pad={{
            horizontal: SIDEBAR_BUTTON_PAD_HORIZONTAL,
            vertical: SIDEBAR_BUTTON_PAD_VERTICAL
          }}
        >
          {typeof label === "string" ? (
            <Text size={TEXT_SIZE_LARGE} style={{ color }}>
              {label}
            </Text>
          ) : (
            React.cloneElement(label, {
              color,
              style: { ...(label.props?.style || {}) }
            })
          )}
        </Box>
      )
    }}
  </Button>
)

const SidebarNav = ({ location }) => {
  const openSectionIndex = location?.state?.openSectionIndex
  const [data] = useState(whatIOfferData)
  const [activeSectionIndex, setActiveSectionIndex] = useState(undefined)
  const [text, setText] = useState("")
  const navRef = useRef(null)
  const [navStuck, setNavStuck] = useState(false)

  const [useNarrowTitles, setUseNarrowTitles] = useState(false)
  useEffect(() => {
    const setFromWidth = () => {
      if (typeof window === "undefined") return
      setUseNarrowTitles(window.innerWidth <= NARROW_LABEL_BREAKPOINT)
    }
    setFromWidth()
    window.addEventListener("resize", setFromWidth)
    return () => window.removeEventListener("resize", setFromWidth)
  }, [])

  const handleActive = useCallback(
    index => {
      setActiveSectionIndex(index)
      setText(data[index]?.text)
      try {
        if (location && typeof location.pathname === "string") {
          navigate(location.pathname, {
            state: { openSectionIndex: index },
            replace: true
          })
        }
      } catch (err) {}
    },
    [data, location]
  )

  useEffect(() => {
    const hash = location?.hash?.replace(/^#/, "")
    if (hash) {
      const idx = data.findIndex(s => s.id === hash)
      if (idx >= 0) {
        handleActive(idx)
        return
      }
    }

    const isLinkStateToOpenSectionIndex = openSectionIndex !== undefined
    const hasActiveIndex = activeSectionIndex !== undefined

    if (isLinkStateToOpenSectionIndex && !hasActiveIndex) {
      handleActive(openSectionIndex)
      return
    }

    const shouldShowDefault = !isLinkStateToOpenSectionIndex && !hasActiveIndex

    if (shouldShowDefault) {
      handleActive(0)
    }
  }, [openSectionIndex, activeSectionIndex, handleActive, data, location])

  useEffect(() => {
    if (typeof window === "undefined") return
    const el = navRef.current
    if (!el) return
    const stickyTop = parseInt(MOBILE_STICKY_TOP, 10) || 0
    const onScroll = () => {
      const top = el.getBoundingClientRect().top
      setNavStuck(top <= stickyTop + 1)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
    }
  }, [])

  const currentIndexForRendering =
    typeof activeSectionIndex === "number" ? activeSectionIndex : 0

  return (
    <Grommet>
      <div className={styles.mobileOnly}>
        <Box>
          <Box
            as="nav"
            role="tablist"
            aria-label="What I Offer sections"
            direction="row"
            gap="small"
            pad={{ vertical: "0px", horizontal: "small" }}
            ref={navRef}
            background={navStuck ? NAV_BG_OPAQUE : NAV_BG}
            className={styles.mobileNav}
            style={{
              top: MOBILE_STICKY_TOP,
              zIndex: MOBILE_NAV_Z,
              paddingBottom: NAV_PADDING_BOTTOM,
              borderRadius: TAB_BORDER_RADIUS
            }}
          >
            {data.map((section, i) => {
              const selected = i === currentIndexForRendering
              const labelText = useNarrowTitles
                ? section.compactName || section.name
                : section.name
              const selectedTextColor = selected
                ? navStuck
                  ? TEXT_COLOR
                  : ACCENT
                : TEXT_COLOR
              const selectedBg = selected
                ? navStuck
                  ? "rgba(255,255,255,0.85)"
                  : SELECT_BG
                : undefined
              return (
                <Button
                  key={section.id}
                  plain
                  onClick={() => handleActive(i)}
                  role="tab"
                  aria-selected={selected}
                  aria-controls={`what-offer-panel-${i}`}
                  aria-label={section.name}
                  className={styles.mobileTabButton}
                >
                  <Box
                    pad={{
                      horizontal: TAB_PAD_HORIZONTAL,
                      vertical: TAB_PAD_VERTICAL
                    }}
                    background={selectedBg}
                    className={styles.mobileTabBox}
                    style={{
                      borderBottom: selected
                        ? `${TAB_SELECTED_BORDER_THICKNESS}px solid ${ACCENT}`
                        : `${TAB_SELECTED_BORDER_THICKNESS}px solid transparent`
                    }}
                  >
                    <Text
                      size={TEXT_SIZE_SMALL}
                      style={{
                        lineHeight: 1,
                        whiteSpace: "normal",
                        color: selectedTextColor
                      }}
                    >
                      {labelText}
                    </Text>
                  </Box>
                </Button>
              )
            })}
          </Box>

          <Box
            pad={PANEL_PAD}
            id={`what-offer-panel-${currentIndexForRendering}`}
            className={styles.panelContent}
          >
            <Box>{text}</Box>
          </Box>
        </Box>
      </div>

      <div className={styles.desktopOnly}>
        <Box direction="row" align="start">
          <Nav
            background={NAV_BG}
            gap="none"
            flex={false}
            className={styles.desktopNav}
            style={{
              top: DESKTOP_STICKY_TOP,
              zIndex: DESKTOP_NAV_Z,
              borderRight: DESKTOP_NAV_BORDER
            }}
          >
            {data.map((section, index) => (
              <SidebarButton
                key={section.id}
                label={<Text color={DESKTOP_LABEL_COLOR}>{section.name}</Text>}
                active={index === activeSectionIndex}
                onClick={() => handleActive(index)}
              />
            ))}
          </Nav>

          <Box pad={PANEL_PAD} className={styles.panelContent}>
            {text}
          </Box>
        </Box>
      </div>
    </Grommet>
  )
}

export default SidebarNav
